import React, { Suspense } from 'react';
import { View, ScrollView, TouchableWithoutFeedback, Modal , Text,TouchableOpacity} from 'react-native';
import UploadSection from './components/UploadSection';
import ProductDetails from './components/ProductDetails';
import ImageUploader from './components/ImageUploader';
// import KeywordsSection from './components/KeywordsSection';
// import ResearchSection from './components/ResearchSection';
import PreviewPane from './components/PreviewPane';
import TemplateFromScratch from './components/TemplateFromScratch';
import { useLandingPage } from './hooks/useLandingPage';
import LandingPageGeneration from './components/LandingPageGeneration';

import styles from '../styles/AppStyles';
import { Navbar } from './components/Navbar';
import FeatureGate from './featureFlags/FeatureGate';
import { useFeatureFlag } from './featureFlags/FeatureFlagsContext';

const MetaAdsPane = React.lazy(() => import('./components/MetaAdsPane'));
const GoogleAdsPane = React.lazy(() => import('./components/GoogleAdsPane'));
const CreativeStudioPane = React.lazy(() => import('./components/CreativeStudioPane'));
const MediaTranslatorPane = React.lazy(() => import('./components/MediaTranslatorPane'));
const SettingsPanel = React.lazy(() => import('./components/SettingsPanel'));
export default function AppContent() {
  const {
    templateFile,
    pickTemplateFile,
    handleFileInput,
    jsonInputRef,
    productDescription,
    setProductDescription,
    price,
    setPrice,
    currency,
    setCurrency,
    currencySymbolPosition,
    setCurrencySymbolPosition,
    productImages,
    handleImagePick,
    handleImageChange,
    handleRemoveImage,
    imageInputRef,
    keywords,
    handleExportKeywords,
    productClassification,
    progress,
    progressStatus,
    skipImageGeneration,
    setSkipImageGeneration,
    skipWidgets,
    setSkipWidgets,
    publishToWordPress,
    setPublishToWordPress,
    wordPressConfigured,
    recentWordPressTemplates,
    selectedWordPressTemplateId,
    setSelectedWordPressTemplateId,
    loadSelectedWordPressTemplate,
    showSettings,
    handleOpenConfig,
    handleCloseConfig,
    handleGenerate,
    handleCancel,
    loading,
    generatedHtml,
    regenerableImageUrls,
    regeneratedImageUrls,
    acceptedImageRegenerationCount,
    regenerateTemplateImage,
    acceptRegeneratedImage,
    downloadTemplateWithRegeneratedImages,
    generatedJson,
    handleExtractProductInfo,
    extractingProductInfo,
    extractProductInfoError,
    fromScratchMode,
    setFromScratchMode,
  } = useLandingPage();

  const creativeStudioEnabled = useFeatureFlag('creativeStudioEnabled');
  const mediaTranslatorEnabled = useFeatureFlag('mediaTranslatorEnabled');

  type TabKey = 'preview' | 'meta' | 'google' | 'creativeStudio' | 'mediaTranslator';

  // tab state: preview | meta | google | creativeStudio | mediaTranslator
  const [activeTab, setActiveTab] = React.useState<TabKey>('preview');
  const [previewDevice, setPreviewDevice] = React.useState<'desktop' | 'mobile'>('desktop');

  // MetaAdsPane state
  const [knowledgeBase, setKnowledgeBase] = React.useState('');
  const [productInfo, setProductInfo] = React.useState('');
  const [mainTexts, setMainTexts] = React.useState<string[]>(Array(5).fill(''));
  const [titles, setTitles] = React.useState<string[]>(Array(5).fill(''));
  const [descriptions, setDescriptions] = React.useState<string[]>(Array(5).fill(''));

  // GoogleAdsPane state (independent from Meta Ads)
  const [googleKnowledgeBase, setGoogleKnowledgeBase] = React.useState('');
  const [googleProductInfo, setGoogleProductInfo] = React.useState('');
  const [googleTitles, setGoogleTitles] = React.useState<string[]>(Array(25).fill(''));
  const [googleLongTitles, setGoogleLongTitles] = React.useState<string[]>(Array(5).fill(''));
  const [googleDescriptions, setGoogleDescriptions] = React.useState<string[]>(Array(5).fill(''));
  const [googleSearchTerms, setGoogleSearchTerms] = React.useState<string[]>(Array(25).fill(''));

  const tabs = React.useMemo((): Array<{ key: TabKey; label: string }> => {
    const next: Array<{ key: TabKey; label: string }> = [
      { key: 'preview', label: 'PREVIEW' },
      { key: 'meta', label: 'META ADS' },
      { key: 'google', label: 'GOOGLE ADS' },
    ];
    if (creativeStudioEnabled) {
      next.push({ key: 'creativeStudio', label: 'CREATIVE STUDIO' });
    }
    if (mediaTranslatorEnabled) {
      next.push({ key: 'mediaTranslator', label: 'MEDIA TRANSLATOR' });
    }
    return next;
  }, [creativeStudioEnabled, mediaTranslatorEnabled]);

  React.useEffect(() => {
    if (activeTab === 'creativeStudio' && !creativeStudioEnabled) {
      setActiveTab('preview');
    }
    if (activeTab === 'mediaTranslator' && !mediaTranslatorEnabled) {
      setActiveTab('preview');
    }
  }, [activeTab, creativeStudioEnabled, mediaTranslatorEnabled]);

  const renderActivePane = () => {
    switch (activeTab) {
      case 'meta':
        return (
          <MetaAdsPane
            knowledgeBase={knowledgeBase}
            setKnowledgeBase={setKnowledgeBase}
            productInfo={productInfo}
            setProductInfo={setProductInfo}
            mainTexts={mainTexts}
            setMainTexts={setMainTexts}
            titles={titles}
            setTitles={setTitles}
            descriptions={descriptions}
            setDescriptions={setDescriptions}
            handleExtractProductInfo={handleExtractProductInfo}
            extractingProductInfo={extractingProductInfo}
            extractProductInfoError={extractProductInfoError}
          />
        );
      case 'google':
        return (
          <GoogleAdsPane
            knowledgeBase={googleKnowledgeBase}
            setKnowledgeBase={setGoogleKnowledgeBase}
            productInfo={googleProductInfo}
            setProductInfo={setGoogleProductInfo}
            titles={googleTitles}
            setTitles={setGoogleTitles}
            longTitles={googleLongTitles}
            setLongTitles={setGoogleLongTitles}
            descriptions={googleDescriptions}
            setDescriptions={setGoogleDescriptions}
            searchTerms={googleSearchTerms}
            setSearchTerms={setGoogleSearchTerms}
            handleExtractProductInfo={handleExtractProductInfo}
            extractingProductInfo={extractingProductInfo}
            extractProductInfoError={extractProductInfoError}
            templateFile={templateFile}
          />
        );
      case 'creativeStudio':
        if (!creativeStudioEnabled) {
          return (
            <PreviewPane
              generatedHtml={generatedHtml}
              regenerableImageUrls={regenerableImageUrls}
              regeneratedImageUrls={regeneratedImageUrls}
              hasAcceptedRegenerations={acceptedImageRegenerationCount > 0}
              onRegenerateImage={regenerateTemplateImage}
              onAcceptRegeneratedImage={acceptRegeneratedImage}
              onDownloadUpdatedTemplate={downloadTemplateWithRegeneratedImages}
              loading={loading}
              progressStatus={progressStatus}
              previewDevice={previewDevice}
            />
          );
        }
        return (
          <CreativeStudioPane
            productDescription={productDescription}
            templateFile={templateFile}
            generatedJson={generatedJson}
            productImages={productImages}
          />
        );
      case 'mediaTranslator':
        if (!mediaTranslatorEnabled) {
          return (
            <PreviewPane
              generatedHtml={generatedHtml}
              regenerableImageUrls={regenerableImageUrls}
              regeneratedImageUrls={regeneratedImageUrls}
              hasAcceptedRegenerations={acceptedImageRegenerationCount > 0}
              onRegenerateImage={regenerateTemplateImage}
              onAcceptRegeneratedImage={acceptRegeneratedImage}
              onDownloadUpdatedTemplate={downloadTemplateWithRegeneratedImages}
              loading={loading}
              progressStatus={progressStatus}
              previewDevice={previewDevice}
            />
          );
        }
        return <MediaTranslatorPane />;
      case 'preview':
      default:
        return (
          <PreviewPane
            generatedHtml={generatedHtml}
            regenerableImageUrls={regenerableImageUrls}
            regeneratedImageUrls={regeneratedImageUrls}
            hasAcceptedRegenerations={acceptedImageRegenerationCount > 0}
            onRegenerateImage={regenerateTemplateImage}
            onAcceptRegeneratedImage={acceptRegeneratedImage}
            onDownloadUpdatedTemplate={downloadTemplateWithRegeneratedImages}
            loading={loading}
            progressStatus={progressStatus}
            previewDevice={previewDevice}
          />
        );
    }
  };

  return (
    <View style={{display:'flex',flexDirection:'column',width:'100%'}}>
      <Navbar />
      <View style={styles.pageWrapper}>
        <View style={styles.leftPane}>
          <ScrollView
            style={styles.container}
            keyboardShouldPersistTaps="handled"
            stickyHeaderIndices={[1]}
          >
            <FeatureGate flag="generationFromScratchEnabled">
              <TemplateFromScratch
                fromScratchMode={fromScratchMode}
                setFromScratchMode={setFromScratchMode}
              />
            </FeatureGate>
            <UploadSection
              templateFile={templateFile}
              pickTemplateFile={pickTemplateFile}
              handleFileInput={handleFileInput}
              jsonInputRef={jsonInputRef}
              fromScratchMode={fromScratchMode}
              recentWordPressTemplates={recentWordPressTemplates}
              selectedWordPressTemplateId={selectedWordPressTemplateId}
              setSelectedWordPressTemplateId={setSelectedWordPressTemplateId}
              loadSelectedWordPressTemplate={loadSelectedWordPressTemplate}
            />
            <ProductDetails
              productDescription={productDescription}
              setProductDescription={setProductDescription}
              price={price}
              setPrice={setPrice}
              currency={currency}
              setCurrency={setCurrency}
              currencySymbolPosition={currencySymbolPosition}
              setCurrencySymbolPosition={setCurrencySymbolPosition}
            />
            <ImageUploader
              productImages={productImages}
              handleImagePick={handleImagePick}
              handleImageChange={handleImageChange}
              handleRemoveImage={handleRemoveImage}
              imageInputRef={imageInputRef}
            />
            {/* <KeywordsSection
              keywords={keywords}
              handleExportKeywords={handleExportKeywords}
            />
            <ResearchSection productClassification={productClassification} /> */}
            <LandingPageGeneration
              progress={progress}
              progressStatus={progressStatus}
              skipImageGeneration={skipImageGeneration}
              setSkipImageGeneration={setSkipImageGeneration}
              skipWidgets={skipWidgets}
              setSkipWidgets={setSkipWidgets}
              publishToWordPress={publishToWordPress}
              setPublishToWordPress={setPublishToWordPress}
              wordPressConfigured={wordPressConfigured}
              handleOpenConfig={handleOpenConfig}
              handleGenerate={handleGenerate}
              handleCancel={handleCancel}
              loading={loading}
              isPreviewPaneActive={activeTab === 'preview'}
              templateFile={templateFile}
              productDescription={productDescription}
              fromScratchMode={fromScratchMode}
            />
          </ScrollView>
        </View>

        {/* Right side */}
        <View style={styles.rightPane}>
          <View style={styles.tabBar} accessibilityRole="tablist">
            <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
              {tabs.map(t => {
                const isActive = activeTab === t.key;
                return (
                  <TouchableOpacity
                    key={t.key}
                    onPress={() => setActiveTab(t.key)}
                    style={[
                      styles.tabButton,
                      isActive ? styles.tabButtonActive : styles.tabButtonInactive,
                    ]}
                    accessibilityRole="tab"
                    accessibilityState={{ selected: isActive }}
                    accessibilityLabel={`Open ${t.label} pane`}
                  >
                    <Text style={isActive ? styles.tabButtonTextActive : styles.tabButtonTextInactive}>
                      {t.label}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
            {activeTab === 'preview' && (
              <View style={{ flexDirection: 'row', alignItems: 'center', marginLeft: 'auto', gap: 8 }}>
                <View style={styles.deviceToggleWrapper} accessibilityRole="tablist">
                  {(['desktop', 'mobile'] as const).map(mode => {
                    const isActive = previewDevice === mode;
                    const label = mode === 'desktop' ? 'Desktop' : 'Mobile';
                    return (
                      <TouchableOpacity
                        key={mode}
                        style={[styles.deviceToggleButton, isActive ? styles.deviceToggleButtonActive : styles.deviceToggleButtonInactive]}
                        onPress={() => setPreviewDevice(mode)}
                        accessibilityRole="tab"
                        accessibilityState={{ selected: isActive }}
                        accessibilityLabel={`Preview in ${label}`}
                      >
                        <Text style={isActive ? styles.deviceToggleTextActive : styles.deviceToggleTextInactive}>
                          {label}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </View>
            )}
          </View>
          {/* Active Pane */}
          <View style={{flex:1}}>
            <Suspense fallback={<View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}><Text style={{ color: '#fff' }}>Loading...</Text></View>}>
              {renderActivePane()}
            </Suspense>
          </View>
        </View>

        {showSettings && (
          <Modal
            visible={showSettings}
            transparent={true}
            animationType="fade"
            onRequestClose={handleCloseConfig}
          >
            <TouchableWithoutFeedback onPress={handleCloseConfig}>
              <View style={styles.modalOverlay}>
                <TouchableWithoutFeedback>
                  <Suspense fallback={<View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}><Text style={{ color: '#fff' }}>Loading...</Text></View>}>
                    <SettingsPanel onCancel={handleCloseConfig} />
                  </Suspense> 
                </TouchableWithoutFeedback>
              </View>
            </TouchableWithoutFeedback>
          </Modal>
        )}
      </View>
    </View>
  );
}
